import { useState } from 'react';
import { Eye, Mail, MoreVertical, RefreshCw } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DataTablePagination } from '@/components/DataTablePagination';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmailDetailDialog } from './EmailDetailDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { SearchInput } from '@/components/ui/search-input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { useEmailQueue, useEmailStats, useResendEmail } from '@/hooks/useTemplates';

type StatusFilter = 'todos' | 'PENDING' | 'PROCESSING' | 'SENT' | 'FAILED';

const DEFAULT_PAGE_SIZE = 20;

function statusBadgeVariant(status: string): 'default' | 'outline' | 'secondary' | 'destructive' {
  switch (status) {
    case 'SENT': return 'default';
    case 'FAILED': return 'destructive';
    case 'PROCESSING': return 'secondary';
    default: return 'outline';
  }
}

function resendLabel(status: string): string {
  if (status === 'SENT') return 'Enviar nova cópia';
  if (status === 'FAILED') return 'Reenviar (clone)';
  return 'Reenviar';
}

function resendSuccessMessage(mode: 'reset_in_place' | 'clone', status: string): string {
  if (mode === 'reset_in_place') return 'Email reenfileirado para envio';
  if (status === 'SENT') return 'Nova cópia enfileirada';
  return 'Email clonado e reenfileirado';
}

export default function EmailQueuePage() {
  const { showSuccess, showError } = useNotification();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('todos');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [detailEmailId, setDetailEmailId] = useState<string | null>(null);

  const statusParam = statusFilter === 'todos' ? undefined : statusFilter;
  const { data: stats } = useEmailStats();
  const { data: queueData, isLoading, error } = useEmailQueue({
    status: statusParam,
    search: search || undefined,
    page,
    page_size: pageSize,
  });

  const resendMutation = useResendEmail();

  const handleResend = (emailId: string, originalStatus: string) => {
    resendMutation.mutate(emailId, {
      onSuccess: (response) => showSuccess(resendSuccessMessage(response.mode, originalStatus)),
      onError: (err) => notifyMutationError(err, showError),
    });
  };

  const items = queueData?.items ?? [];
  const total = queueData?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  const handleTabChange = (value: string) => {
    setStatusFilter(value as StatusFilter);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fila de Emails"
        subtitle="Monitore, inspecione e reenvie emails."
        icon={<Mail />}
      />

      {/* Filtros — soltos, sem Card */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput
          value={search}
          onChange={(val) => { setSearch(val); setPage(1); }}
          placeholder="Buscar por email ou assunto..."
          className="w-full sm:max-w-sm"
        />

        <div className="flex items-center gap-2">
          <Tabs value={statusFilter} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="todos">Todos ({(stats?.pending ?? 0) + (stats?.processing ?? 0) + (stats?.sent ?? 0) + (stats?.failed ?? 0)})</TabsTrigger>
              <TabsTrigger value="PENDING">Pendentes ({stats?.pending ?? 0})</TabsTrigger>
              <TabsTrigger value="SENT">Enviados ({stats?.sent ?? 0})</TabsTrigger>
              <TabsTrigger value="FAILED">Falhas ({stats?.failed ?? 0})</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Dados */}
      {isLoading && <Spinner size={32} label="Carregando fila de e-mails" />}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}
      {!isLoading && !error && items.length === 0 && (
        <EmptyState
          icon={<Mail />}
          title="Nenhum email encontrado"
          description="Tente ajustar o filtro ou a busca."
        />
      )}
      {items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Destinatário</TableHead>
                  <TableHead className="hidden md:table-cell">Assunto</TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  <TableHead className="hidden sm:table-cell text-center">Tentativas</TableHead>
                  <TableHead className="hidden lg:table-cell">Criado em</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-sm">
                      <div className="flex flex-col gap-0.5">
                        <span>{e.to_email}</span>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground sm:hidden">
                          <Badge variant={statusBadgeVariant(e.status)} className="text-[11px] px-1.5 py-0">{e.status}</Badge>
                          <span>{new Date(e.created_at).toLocaleDateString('pt-BR')}</span>
                          {e.attempts > 0 && <span>{e.attempts}/{e.max_attempts} tent.</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden break-words text-sm md:table-cell">{e.subject}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant={statusBadgeVariant(e.status)}>{e.status}</Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-center text-sm">
                      {e.attempts}/{e.max_attempts}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {new Date(e.created_at).toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetailEmailId(e.id)}>
                            <Eye className="mr-2 h-4 w-4" />
                            Detalhes
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleResend(e.id, e.status)}
                            disabled={resendMutation.isPending}
                          >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            {resendLabel(e.status)}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>

          {totalPages > 1 && (
            <div className="border-t p-2">
              <DataTablePagination
                page={page}
                pageSize={pageSize}
                total={total}
                pages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              />
            </div>
          )}
        </Card>
      )}

      <EmailDetailDialog
        emailId={detailEmailId}
        onOpenChange={(open) => { if (!open) setDetailEmailId(null); }}
      />
    </div>
  );
}
